import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealMetrics } from '@/hooks/useDealMetrics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Video, DollarSign, FileText, ExternalLink, Loader2, Plus, Trash2, StickyNote, ArrowUpRight, Tags } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDateTimeShort } from '@/lib/dateUtils';
import { UpsellConversionModal } from './UpsellConversionModal';
import { DealTagsSelector } from './DealTagsSelector';
interface Note {
  id: string;
  content: string;
  created_at: string;
  creator?: {
    display_name?: string;
  };
}

interface DealOverviewProps {
  dealId: string;
  dealName: string;
  stage: string;
  totalAmount: number;
  currency?: string;
  contactName: string;
  contactId?: string;
}

interface EligibilityDocument {
  id: string;
  storage_path: string;
  name: string;
  created_at: string;
  status: string;
}

export function DealOverview({
  dealId,
  dealName,
  stage,
  totalAmount,
  currency = 'USD',
  contactName,
  contactId
}: DealOverviewProps) {
  const { t } = useTranslation();
  const { metrics, loading } = useDealMetrics(dealId);
  const [eligibilityDoc, setEligibilityDoc] = useState<EligibilityDocument | null>(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  useEffect(() => {
    loadEligibilityDocument();
  }, [contactId]);

  const loadEligibilityDocument = async () => {
    if (!contactId) {
      setEligibilityLoading(false);
      return;
    }
    
    setEligibilityLoading(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('id, storage_path, name, created_at, status')
        .eq('owner_type', 'contact')
        .eq('owner_id', contactId)
        .eq('category', 'Eligibility Assessment')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error loading eligibility document:', error);
      } else {
        setEligibilityDoc(data);
      }
    } catch (err) {
      console.error('Error loading eligibility document:', err);
    } finally {
      setEligibilityLoading(false);
    }
  };

  const handleViewPdf = async () => {
    if (!eligibilityDoc?.storage_path) return;
    
    setPdfLoading(true);
    try {
      // Determine bucket based on storage_path
      const storagePath = eligibilityDoc.storage_path;
      let bucket = 'documents';
      let key = storagePath;
      
      if (storagePath.startsWith('eligibility-pdfs/')) {
        bucket = 'eligibility-pdfs';
        key = storagePath.replace('eligibility-pdfs/', '');
      }

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(key, 3600);

      if (error) {
        console.error('Error generating PDF URL:', error);
        alert(t('dealOverview.failedToLoadPdf'));
        return;
      }

      // Open in new tab
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing PDF:', err);
      alert(t('dealOverview.errorLoadingPdf'));
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading || !metrics) {
    return <div className="flex justify-center p-8">{t('dealOverview.loadingOverview')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{metrics.total_calls}</div>
                <div className="text-sm text-muted-foreground">{t('dealOverview.calls')}</div>
              </div>
              <Phone className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{metrics.total_meetings}</div>
                <div className="text-sm text-muted-foreground">{t('dealOverview.meetings')}</div>
              </div>
              <Video className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{metrics.total_payments}</div>
                <div className="text-sm text-muted-foreground">{t('dealOverview.payments')}</div>
              </div>
              <DollarSign className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{metrics.total_contracts}</div>
                <div className="text-sm text-muted-foreground">{t('dealOverview.contracts')}</div>
              </div>
              <FileText className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deal Tags */}
      {stage && !['won', 'lost'].includes(stage) && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Tags className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="font-medium mb-3">{t('dealOverview.tags', 'Tags do Estágio')}</div>
                <DealTagsSelector dealId={dealId} currentStage={stage} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Eligibility Assessment Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <div className="font-medium">{t('dealOverview.eligibilityAssessment')}</div>
                {eligibilityLoading ? (
                  <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                ) : eligibilityDoc ? (
                  <div className="text-sm text-muted-foreground">
                    {t('dealOverview.submitted')}: {new Date(eligibilityDoc.created_at).toLocaleDateString()}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">{t('dealOverview.noAssessmentSubmitted')}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {eligibilityLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : eligibilityDoc ? (
                <>
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    {t('dealOverview.available')}
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleViewPdf}
                    disabled={pdfLoading}
                  >
                    {pdfLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-1" />
                        {t('dealOverview.viewPdf')}
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                  {t('dealOverview.notAvailable')}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upsell Conversion Button */}
      {stage === 'won' && contactId && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ArrowUpRight className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium">{t('dealOverview.upsellConversion', 'Conversão para Upsell')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('dealOverview.upsellDescription', 'Transfira pagamentos para um novo contrato')}
                  </div>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowUpsellModal(true)}
              >
                <ArrowUpRight className="w-4 h-4 mr-2" />
                {t('dealOverview.convertToUpsell', 'Converter para Upsell')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upsell Conversion Modal */}
      {contactId && (
        <UpsellConversionModal
          open={showUpsellModal}
          onOpenChange={setShowUpsellModal}
          originDeal={{
            id: dealId,
            name: dealName,
            total_amount: totalAmount,
            currency: currency,
            contactId: contactId,
          }}
        />
      )}
    </div>
  );
}

export interface NotesSectionProps {
  notes: Note[];
  notesLoading: boolean;
  createNote: (data: { content: string }) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  isEditable?: boolean;
}

export function NotesSection({ notes, notesLoading, createNote, deleteNote, isEditable = true }: NotesSectionProps) {
  const { t } = useTranslation();
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    
    setSaving(true);
    try {
      await createNote({ content: newNoteContent.trim() });
      setNewNoteContent('');
      setShowAddNote(false);
    } catch (err) {
      console.error('Error creating note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm(t('dealOverview.deleteNoteConfirm'))) return;
    await deleteNote(noteId);
  };

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{t('dealOverview.notes')}</CardTitle>
            {notes.length > 0 && (
              <Badge variant="secondary" className="text-xs">{notes.length}</Badge>
            )}
          </div>
          {isEditable && !showAddNote && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowAddNote(true)}
              className="h-7 px-2"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('dealOverview.add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {/* Add Note Form */}
        {isEditable && showAddNote && (
          <div className="mb-4 space-y-2">
            <Textarea
              placeholder={t('dealOverview.writeNote')}
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex gap-2 justify-end">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowAddNote(false);
                  setNewNoteContent('');
                }}
              >
                {t('dealOverview.cancel')}
              </Button>
              <Button 
                size="sm" 
                onClick={handleAddNote}
                disabled={saving || !newNoteContent.trim()}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('dealOverview.save')}
              </Button>
            </div>
          </div>
        )}

        {/* Notes List */}
        {notesLoading ? (
          <div className="text-sm text-muted-foreground py-2">{t('dealOverview.loadingNotes')}</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">{t('dealOverview.noNotesYet')}</div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div 
                key={note.id} 
                className="group border rounded-lg p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                  <div className="text-xs text-muted-foreground">
                    {formatDateTimeShort(note.created_at)}
                    {note.creator?.display_name && (
                      <span> • {note.creator.display_name}</span>
                    )}
                  </div>
                  {isEditable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note.id)}
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}